import { useId, useState } from 'react';
import { PROSPECT_NOTES_COUNT_DOWN_FROM, PROSPECT_NOTES_MAX_LENGTH } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ProspectNoteEditorProps {
  /** The note as it stands; null when there's none. */
  note: string | null;
  /** Save what was typed; blank clears the note (the server trims it). */
  onSave: (note: string) => void;
  onCancel: () => void;
  isSaving: boolean;
  /** Why the last save was refused, as the server said it. */
  error: string | null;
  autoFocus?: boolean;
}

/**
 * The one editor for a person's note — who they know, what matters to them — on their page and from the
 * book's row. Held to the server's limit, counting down the characters left as it nears it. Presentational.
 */
export function ProspectNoteEditor({ note, onSave, onCancel, isSaving, error, autoFocus = false }: ProspectNoteEditorProps) {
  const [draft, setDraft] = useState(note ?? '');
  const id = useId();
  const left = PROSPECT_NOTES_MAX_LENGTH - draft.length;

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(draft);
      }}
    >
      <label htmlFor={id} className="sr-only">
        Note
      </label>
      <textarea
        id={id}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        maxLength={PROSPECT_NOTES_MAX_LENGTH}
        rows={4}
        autoFocus={autoFocus}
        placeholder="Tony is friends with her husband. Served in the Air Force together."
        className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-[13px] leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      {left <= PROSPECT_NOTES_COUNT_DOWN_FROM && (
        <p className={cn('text-xs tabular-nums', left === 0 ? 'text-destructive' : 'text-muted-foreground')}>
          {left} {left === 1 ? 'character' : 'characters'} left
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" className="h-10" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="h-10" disabled={isSaving}>
          Save note
        </Button>
      </div>
    </form>
  );
}
